import { mutateTransportation } from "@/api/transportation/useTransportationQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormSelect } from "@/features/standard/Form/FormSelect";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import { Main } from "@/features/standard/Main";
import customAlert from "@/features/ui/alert/alert";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, Select, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  ScmApiTransportationTransportationIdPutRequest,
  TransportationGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Update = Object.assign({}, Main, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  DateInput: DateInput,
});

interface TransportationStatusFormProps {
  formatterProps: TransportationGet200ResponseRowsInner | undefined;
}

export const TransportationStatusForm = (params: TransportationStatusFormProps) => {
  const { formatterProps } = params;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate: updateMutate } = useMutation(
    (params: ScmApiTransportationTransportationIdPutRequest) =>
      mutateTransportation
        .update(params)
        .mutationFn(params as ScmApiTransportationTransportationIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["transportation"]);
        closeModal(formatterProps);
        customAlert(
          t("message.changeSuccess", { item: t("transportation.status") }),
          t("common.changeSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("transportation.status") }),
          t("common.changeFail"),
          "red",
        );
      },
    },
  );

  const { closeModal } = useModal();

  const form = useForm({
    initialValues: {
      partnerCode: formatterProps?.partnerCode,
      status: formatterProps?.status,
    },
  });

  const onSubmit = async () => {
    try {
      updateMutate({
        transportationId: formatterProps?.id ?? 0,
        transportationGetRequest: {
          direction: formatterProps?.direction!,
          code: formatterProps?.code!,
          partnerCode: form.values.partnerCode!,
          status: form.values.status!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("transportation.status") }),
        t("common.changeFail"),
        "red",
      );
    }
  };

  return (
    <>
      <Update.Wrapper>
        <Select
          label={t("transportation.status")}
          placeholder={t("placeholder.select", { item: t("transportation.status") })}
          data={[
            { label: t("transportationStatus.SCHEDULED"), value: "SCHEDULED" },
            { label: t("transportationStatus.LOADING"), value: "LOADING" },
            { label: t("transportationStatus.DONE"), value: "DONE" },
            { label: t("transportationStatus.ON_ROAD"), value: "ON_ROAD" },
            { label: t("transportationStatus.ERROR"), value: "ERROR" },
          ]}
          defaultValue={formatterProps?.status}
          {...form.getInputProps("status")}
          error={form.isDirty("status") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("status") ? "cadetblue !important" : "inherit",
              borderColor: form.isDirty("status") ? "cadetblue !important" : "inherit",
            },
            error: { fontSize: 11, color: "cadetblue" },
          }}
          withinPortal
        />
        <Update.ButtonBox>
          <Update.Button color="gray" onClick={() => closeModal(null)}>
            {t("common.cancel")}
          </Update.Button>
          <Update.Button onClick={onSubmit}>
            {t("common.change", { item: t("transportation.status") })}
          </Update.Button>
        </Update.ButtonBox>
      </Update.Wrapper>
    </>
  );
};
